import type { IObservableArray } from 'mobx';
import { runInAction } from 'mobx';
import { useCallback } from 'react';

import type { Campaign, Targetable, Targeting } from '@feathr/blackbox';
import { useStore } from '@feathr/extender/state';

import { getInterestsTargetings } from '../Targetings.utils';
import { determineInterestsMatchMode, getGroupIdForInterest } from './Interests.utils';

interface IProps {
  campaign: Campaign;
  targetings: IObservableArray<Targeting>;
  filteredInterests: Targetable[];
}

interface IUseElectedInterestsReturn {
  handleInterestChange: (checked: boolean | undefined, interestId: string) => void;
  selectedInterestIds: string[];
  targetings: IObservableArray<Targeting>;
}

export function useSelectedInterests({
  campaign,
  targetings,
  filteredInterests,
}: Readonly<IProps>): IUseElectedInterestsReturn {
  const { Targetings } = useStore();

  const interestTargetings = getInterestsTargetings(targetings);
  const matchMode = determineInterestsMatchMode(interestTargetings);

  // Derive selected interests directly from targetings
  const selectedInterestIds = interestTargetings.map(
    (targeting) => targeting.get('target_data') as string,
  );

  const handleInterestChange = useCallback(
    (checked: boolean | undefined, interestId: string): void => {
      const isChecked = checked ?? false;
      const interest = filteredInterests.find((i) => i.id === interestId);

      if (!interest) {
        return;
      }

      if (isChecked) {
        // Create a new targeting
        const model = Targetings.create({
          parent: campaign.get('id'),
          kind: 'interest',
          included: true,
        });

        const groupId = getGroupIdForInterest(matchMode);

        // Add the targeting to the targetings array and populate it
        runInAction(() => {
          targetings.push(model);

          // Then populate it (this makes it dirty!)
          model.set({
            name: interest.get('name'),
            target_data: interest.id,
            group: groupId,
          });
        });
      } else {
        // Remove existing targeting - only find non-archived targetings
        const existingTargeting = targetings.find((targeting) => {
          return (
            targeting.get('kind') === 'interest' &&
            targeting.get('target_data') === interestId &&
            !targeting.get('is_archived')
          );
        });

        if (existingTargeting) {
          runInAction(() => {
            // If the targeting is not ephemeral, archive it
            if (!existingTargeting.isEphemeral) {
              // Archive the targeting lets it get set to dirty
              existingTargeting.set({ is_archived: true });
            } else {
              // Remove the targeting from the targetings array
              targetings.remove(existingTargeting);
              Targetings.remove(existingTargeting.id);
            }
          });
        }
      }
    },
    [filteredInterests, campaign, targetings, Targetings],
  );

  return {
    handleInterestChange,
    selectedInterestIds,
    targetings,
  };
}

export default useSelectedInterests;
